import { Box, CircularProgress, Typography } from '@mui/material'
import { BaseTable } from 'Features/Table/BaseTable.view'
import {
  SOROperationalPlanViewProps,
  SOROperationaPlanData,
} from './SOROperationalPlans.types'
import { useMemo } from 'react'
import {
  GridRenderCellParams,
  GridRowParams,
  GridActionsCellItem,
} from '@mui/x-data-grid-premium'
import { Check, CheckCircle } from '@mui/icons-material'
import { OPERTAUIONAL_PLAN_STATUS } from 'Common/Constants/Constants'
import { searchTextOperator } from 'Common/MUITable/SearchText.operator'

export const SOROperationalPlanTable = ({
  isLoading,
  data,
  handleRowClick,
  handleApprovePlan,
}: SOROperationalPlanViewProps) => {
  const getStatusColumn = (row: SOROperationaPlanData) => {
    const { status, publishedWorkcentres, totalWorkcentres } = row
    switch (status) {
      case OPERTAUIONAL_PLAN_STATUS.PUBLISHED:
        return (
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography
              variant={'button'}
              style={{ color: 'green', fontWeight: 'bold' }}
            >
              {status} ({publishedWorkcentres} / {totalWorkcentres})
            </Typography>
            <Check style={{ color: 'green', fontWeight: 'bold' }} />
          </Box>
        )
      case OPERTAUIONAL_PLAN_STATUS.INITIATED:
        return (
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography
              variant={'button'}
              style={{ color: 'orange', fontWeight: 'bold' }}
            >
              {status}...
            </Typography>
            <CircularProgress
              style={{ color: 'orange', fontWeight: 'bold' }}
              size={18}
            />
          </Box>
        )
      case OPERTAUIONAL_PLAN_STATUS.APPROVED:
        return (
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography
              variant={'button'}
              style={{ color: 'green', fontWeight: 'bold' }}
            >
              {status}
            </Typography>
            <Check style={{ color: 'green', fontWeight: 'bold' }} />
          </Box>
        )
    }
  }

  const weeks = useMemo(() => {
    return data.map((row) => {
      return {
        value: row.planStartWeek,
        label: row.planStartWeek,
      }
    })
  }, [data])

  const specialSearch = (arr: string[], label: string) => {
    return {
      ...searchTextOperator[0],
      InputComponentProps: { data: arr, label },
    }
  }

  const columns = useMemo(
    () => [
      {
        headerName: 'Plan name',
        field: 'planName',
        flex: 1,
      },
      {
        headerName: 'Status',
        field: 'status',
        flex: 1,
        type: 'singleSelect' as const,
        renderCell: (params: GridRenderCellParams<any, string>) => {
          return <>{getStatusColumn(params.row)}</>
        },
      },
      {
        headerName: 'Plan start week',
        field: 'planStartWeek',
        flex: 1,
        valueOptions: weeks,
        filterOperators: [
          specialSearch(
            weeks.map((week) => week.value),
            'Week'
          ),
        ],
      },
      {
        headerName: 'Generated by',
        field: 'generatedBy',
        flex: 1,
      },
      {
        field: 'actions',
        type: 'actions' as const,
        headerName: 'Actions',
        width: 100,
        cellClassName: 'actions',
        getActions: (params: GridRowParams<SOROperationaPlanData>) => {
          const isPublished =
            params.row.status === OPERTAUIONAL_PLAN_STATUS.PUBLISHED

          return isPublished
            ? [
                <GridActionsCellItem
                  icon={<CheckCircle />}
                  label="Approve plan"
                  onClick={() => handleApprovePlan(params.row)}
                />,
              ]
            : []
        },
      },
    ],
    [data]
  )
  // No-op functions for BaseTable required props (read-only table)
  const updateTableData = () => {
    // This is a read-only table, no updates needed
  }

  const uploadData = () => {
    // No upload functionality needed
  }

  const handleRowClickWrapper = (params: any, event: any, details: any) => {
    handleRowClick(params.id as string)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadData={uploadData}
        isUploading={false}
        isSaving={false}
        checkBoxSelection={false}
        showTableToolBar={false}
        handleRowClick={handleRowClickWrapper}
        pinnedColumns={['planName']}
        downloadFileName="PublishedOperationalPlans"
        getRowId={(row) => row.planId}
        density="standard"
      />
    </Box>
  )
}
